import { useEffect, useState } from 'react';

import { ApiError, KIOSK_HEARTBEAT_INTERVAL_MS, reportKioskHeartbeat } from './api';
import { getDeviceCode, subscribeDeviceCode } from './device-identity';

/**
 * 回報狀態。
 *
 * `unregistered` 與其他失敗要分開：前者是**永久性**的（後端根本不認得這個代號），
 * 重試一萬次也不會變成功；後者多半是網路或後端暫時不在，重試才有意義。
 */
export type HeartbeatStatus =
  | { state: 'unbound' }
  | { state: 'ok'; deviceCode: string }
  | { state: 'unregistered'; deviceCode: string }
  | { state: 'unreachable'; deviceCode: string };

/** 後端對「代號不存在／非 kiosk／已退役」的回應碼。 */
const NOT_FOUND_CODE = 'KIOSK_DEVICE_NOT_FOUND';

/**
 * 連線失敗要隔幾次才再叫一次。
 *
 * 後端離線時每 30 秒印一行警告，跑一晚會累積上千行，真正的錯誤就被沖掉了。
 */
const WARN_EVERY = 20;

/**
 * KIOSK 存活回報 hook。
 *
 * device_code 取自 runtime 綁定（lib/device-identity）——同一份 build 跑多台
 * 時，各台各自綁定不同 code 而能在儀表板分辨。**未綁定時不回報**（避免多台撞
 * 同一預設 code 互相覆蓋），維運可進 /device-setup 設定。綁定變更會即時重啟回報。
 *
 * 已綁定時：掛載立即回報一次，之後每 {@link KIOSK_HEARTBEAT_INTERVAL_MS} 一次，
 * 讓 CMS 設備儀表板（AC-H-3）顯示本機台在線。
 *
 * ## 認不得的代號會停止回報
 *
 * 綁定當下有驗證過（/device-setup 走 device-check），但綁定會**事後失效**——
 * 設備在 CMS 被退役、或後台資料被重建，代號就不再存在。此時後端回 404
 * {@link NOT_FOUND_CODE}，而原本的做法是每 30 秒再試一次、只留一行 console.warn：
 * 機台永遠不會恢復，畫面上也看不出任何異狀，只有 CMS 那端顯示離線。
 *
 * 現在遇到這個碼就停止輪詢並回報 `unregistered`，維運進 /device-setup 會直接看到
 * 原因。回報失敗一律不影響購票主流程——心跳只是給儀表板看的。
 */
export function useKioskHeartbeat(): HeartbeatStatus {
  const [deviceCode, setDeviceCode] = useState<string | null>(getDeviceCode());
  const [status, setStatus] = useState<HeartbeatStatus>({ state: 'unbound' });

  // 訂閱綁定變更（/device-setup 儲存後即時切換回報目標）
  useEffect(() => subscribeDeviceCode(() => setDeviceCode(getDeviceCode())), []);

  useEffect(() => {
    if (deviceCode === null) {
      setStatus({ state: 'unbound' });
      console.warn('[kiosk-heartbeat] 未綁定 device_code，略過回報；至 /device-setup 設定機台代號');
      return;
    }

    let cancelled = false;
    let failures = 0;
    let timer: number | null = null;
    const appVersion = import.meta.env.VITE_APP_VERSION as string | undefined;

    const stop = (): void => {
      if (timer !== null) window.clearInterval(timer);
      timer = null;
    };

    const beat = (): void => {
      reportKioskHeartbeat(deviceCode, appVersion).then(
        () => {
          if (cancelled) return;
          failures = 0;
          setStatus({ state: 'ok', deviceCode });
        },
        (err: unknown) => {
          if (cancelled) return;

          const code =
            err instanceof ApiError && err.status === 404
              ? (err.problem?.code as string | undefined)
              : undefined;

          if (code === NOT_FOUND_CODE) {
            // 永久性失敗：停掉輪詢，錯誤只印一次且講清楚要做什麼
            stop();
            setStatus({ state: 'unregistered', deviceCode });
            console.error(
              `[kiosk-heartbeat] 機台代號 ${deviceCode} 不存在於後台（或已退役／非購票機類型），` +
                '已停止回報。請在 CMS「設備管理」登錄此代號，或於 /device-setup 改綁其他代號。',
            );
            return;
          }

          setStatus({ state: 'unreachable', deviceCode });
          if (failures % WARN_EVERY === 0) {
            console.warn(`[kiosk-heartbeat] 回報失敗（第 ${failures + 1} 次，將持續重試）：`, err);
          }
          failures += 1;
        },
      );
    };

    beat(); // 綁定後立即回報
    timer = window.setInterval(beat, KIOSK_HEARTBEAT_INTERVAL_MS);
    return () => {
      cancelled = true;
      stop();
    };
  }, [deviceCode]);

  return status;
}
